import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import { subscribeToNewsletter } from '../services/emailMarketingService';
import { sanitizeEmail, sanitizeText } from '../utils/sanitize';
import { formatApiError } from '../utils/apiError';
import { buildSubscribePayload, NewsletterSource } from '../utils/newsletter';

interface NewsletterFormProps {
  variant?: 'compact' | 'full';
  source?: NewsletterSource;
  /** A feliratkozás gomb felirata (pl. a főoldali blokkból: „Kérem a kupont”). */
  buttonLabel?: string;
}

const SUCCESS_TEXT = 'Megerősítő e-mailt küldtünk — a megerősítés után megkapod a 10%-os kuponodat.';

const NewsletterForm: React.FC<NewsletterFormProps> = ({
  variant = 'compact',
  source = 'footer',
  buttonLabel = 'Feliratkozás',
}) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await subscribeToNewsletter(
        buildSubscribePayload({
          email: sanitizeEmail(email.trim()),
          firstName: sanitizeText(firstName.trim()),
          lastName: sanitizeText(lastName.trim()),
          source,
        }),
      );
      setSuccess(result.message || SUCCESS_TEXT);
      setEmail('');
      setFirstName('');
      setLastName('');
    } catch (err: unknown) {
      setError(formatApiError(err, { email: 'E-mail cím' }));
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'compact') {
    return (
      <Box>
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Keresztnév"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={loading}
                sx={{
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                }}
              />
              <TextField
                fullWidth
                size="small"
                placeholder="Vezetéknév"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={loading}
                sx={{
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                }}
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              type="email"
              placeholder="Email címed"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              InputProps={{
                startAdornment: <EmailIcon sx={{ color: 'text.secondary', mr: 1 }} />,
              }}
              sx={{
                backgroundColor: 'background.paper',
                borderRadius: 1,
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !email}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : buttonLabel}
            </Button>
          </Box>
        </form>
      </Box>
    );
  }

  // Full variant with name fields
  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Iratkozz fel hírlevelünkre!
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Értesülj elsőként új termékeinkről, akcióinkról és exkluzív ajánlatainkról.
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              fullWidth
              label="Keresztnév"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={loading}
            />
            <TextField
              fullWidth
              label="Vezetéknév"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={loading}
            />
          </Box>

          <TextField
            fullWidth
            type="email"
            label="Email cím *"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            InputProps={{
              startAdornment: <EmailIcon sx={{ color: 'text.secondary', mr: 1 }} />,
            }}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading || !email}
            fullWidth
          >
            {loading ? <CircularProgress size={24} /> : buttonLabel}
          </Button>

          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
            A feliratkozással elfogadod az{' '}
            <a href="/adatvedelmi-nyilatkozat" style={{ textDecoration: 'underline' }}>
              adatvédelmi nyilatkozatunkat
            </a>.
          </Typography>
        </Box>
      </form>
    </Paper>
  );
};

export default NewsletterForm;
